import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Users, Calendar, FileText, ChevronRight, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyClasses() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const response = await fetch(`${API_URL}/api/staff-portal/my-classes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setClasses(data.classes || []);
      } else if (response.status === 401) {
        localStorage.removeItem('staffToken');
        navigate('/staff-portal');
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error('Failed to fetch classes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="my-classes-page">
      {/* Header */}
      <header className="bg-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/staff-portal/dashboard" className="p-2 hover:bg-indigo-600 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">My Classes</h1>
              <p className="text-indigo-200 text-sm">Your scheduled classes from routine</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {classes.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Classes Scheduled</h3>
              <p className="text-gray-500">
                You don&apos;t have any classes in the routine yet. Please contact the administrator to add your schedule.
              </p>
            </CardContent>
          </Card>
        ) : (
          classes.map((cls) => (
            <Card key={cls.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm font-medium">
                        {cls.course_name}
                      </span>
                      {cls.semester_name && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">
                          {cls.semester_name}
                        </span>
                      )}
                      {cls.section_name && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-sm">
                          Section {cls.section_name}
                        </span>
                      )}
                    </div>
                    
                    {cls.subject_name && (
                      <p className="text-gray-700 font-medium mb-2">
                        <BookOpen className="inline h-4 w-4 mr-1 text-gray-400" />
                        {cls.subject_name}
                        {cls.subject_code && <span className="text-gray-400 ml-2">({cls.subject_code})</span>}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {cls.student_count || 0} Students
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {cls.schedule_count || 1} periods/week
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Link
                      to={`/staff-portal/mark-attendance/${cls.id}`}
                      className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm"
                    >
                      <Calendar className="h-4 w-4" />
                      Attendance
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                    <Link
                      to={`/staff-portal/enter-marks/${cls.id}`}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm"
                    >
                      <FileText className="h-4 w-4" />
                      Enter Marks
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </main>
    </div>
  );
}
